import React, { useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { FormPhotoUpload, NeoCard, NeoInput, NeoButton, NeoBadge, OverlayLoader } from "../../../../components/Common";
import { MaskHelper, AddressHelper } from "../../../../utils/helpers";
import { ROLE_OPTIONS, ROLE_LABELS, USER_DEFAULTS } from "../constants";

const userSchema = Yup.object().shape({
    fullName: Yup.string()
        .min(2, "Nome muito curto")
        .required("O nome completo é obrigatório"),
    email: Yup.string()
        .email("E-mail inválido")
        .required("O e-mail é obrigatório"),
    platformRole: Yup.string()
        .oneOf(ROLE_OPTIONS.map(r => r.value), "Papel de acesso inválido")
        .required("O papel de acesso é obrigatório"),
    position: Yup.string().nullable(),
    phone: Yup.string().nullable(),
    photoUrl: Yup.string().nullable(),
    status: Yup.string().oneOf(["active", "inactive"]).default("active"),
    password: Yup.string().when("id", {
        is: (val) => !val,
        then: () => Yup.string().required("Senha é obrigatória para criar acesso").min(6, "A senha deve ter no mínimo 6 caracteres"),
        otherwise: () => Yup.string().nullable()
    }),
    zipCode: Yup.string().nullable(),
    street: Yup.string().nullable(),
    number: Yup.string().nullable(),
    neighborhood: Yup.string().nullable(),
    city: Yup.string().nullable(),
    state: Yup.string().nullable(),
    complement: Yup.string().nullable()
});

/**
 * UserEditor
 * Formulário principal de usuários administrativos do SaaS.
 * Shape alinhado com o backend: fullName, platformRole, position, etc.
 */
const UserEditor = ({ user, onSave, isSaving, onChange }) => {
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: USER_DEFAULTS,
        validationSchema: userSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        }
    });

    // Sincroniza estado inicial do formulário quando o user muda
    useEffect(() => {
        if (user) {
            // Achatar o address do backend (objeto) para os campos flat do formik
            const addr = user.address || {};
            formik.resetForm({
                values: {
                    ...USER_DEFAULTS,
                    ...user,
                    zipCode: user.zipCode || addr.zipCode || "",
                    street: user.street || addr.street || "",
                    number: user.number || addr.number || "",
                    neighborhood: user.neighborhood || addr.neighborhood || "",
                    city: user.city || addr.city || "",
                    state: user.state || addr.state || "",
                    complement: user.complement || addr.complement || "",
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    // Busca de CEP automática
    const handleCepChangeInternal = async (e) => {
        let cepVal = e.target.value;
        cepVal = MaskHelper.cep(cepVal);
        formik.setFieldValue("zipCode", cepVal);

        const cleanCep = cepVal.replace(/\D/g, "");
        if (cleanCep.length === 8) {
            try {
                const address = await AddressHelper.fetchByCep(cleanCep);
                if (address) {
                    formik.setFieldValue("street", address.street || "");
                    formik.setFieldValue("neighborhood", address.neighborhood || "");
                    formik.setFieldValue("city", address.city || "");
                    formik.setFieldValue("state", address.state || "");
                }
            } catch (error) {
                console.error("Erro ao buscar CEP:", error);
            }
        }
    };

    if (!user) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-card-account-details-outline"></i>
                        </div>
                    </div>
                    <h5>Selecione um colaborador da equipe</h5>
                    <p className="text-muted">Escolha um colaborador na lista ao lado ou cadastre um novo acesso administrativo global.</p>
                </div>
            </div>
        );
    }

    const displayName = formik.values.fullName || "Novo Colaborador";
    const roleLabel = ROLE_LABELS[formik.values.platformRole] || formik.values.platformRole || "";

    return (
        <NeoCard
            title={
                <span className="d-flex align-items-center gap-2">
                    <span className="text-truncate">{formik.values.id ? displayName : "Novo Colaborador"}</span>
                    {formik.values.id && (
                        <NeoBadge color={formik.values.status === "active" ? "success" : "danger"}>
                            {formik.values.status === "active" ? "Ativo" : "Bloqueado"}
                        </NeoBadge>
                    )}
                    {formik.values.id && roleLabel && (
                        <NeoBadge color="accent">{roleLabel}</NeoBadge>
                    )}
                </span>
            }
            subtitle="Dados completos e permissões de acesso"
            headerIcon="mdi-account-tie"
            headerActions={
                <div className="d-flex gap-2 flex-shrink-0">
                    {formik.values.id && (
                        <NeoButton
                            variant="soft-primary"
                            icon="mdi-eye-outline"
                            onClick={() => navigate(`/platform/users/${formik.values.id}`)}
                        >
                            Ver Perfil
                        </NeoButton>
                    )}
                    <NeoButton
                        variant="primary"
                        loading={isSaving}
                        loadingText="Salvando..."
                        onClick={formik.handleSubmit}
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
            className="overflow-hidden rounded-0 position-relative animate__animated animate__fadeIn"
            bodyClassName="p-4"
            noBorder={true}
        >
            <OverlayLoader show={isSaving} label="Salvando colaborador..." />

            <Row className="mb-4">
                <Col lg={12}>
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-account-circle-outline me-2 font-size-16 text-primary"></i>
                        Perfil e Acesso
                    </h6>
                </Col>

                <Col md="3" className="text-center mb-4 mb-md-0 d-flex flex-column align-items-center justify-content-center">
                    <FormPhotoUpload
                        photoPreview={formik.values.photoUrl}
                        onChange={(url) => {
                            formik.setFieldValue("photoUrl", url);
                            onChange(true);
                        }}
                        size="lg"
                        roundedCircle={false}
                    />
                </Col>

                <Col md="9">
                    <Row>
                        <Col md="6" className="mb-3">
                            <NeoInput
                                label="Nome Completo *"
                                name="fullName"
                                type="text"
                                flat={true}
                                placeholder="Ex: Felipe Macedo"
                                error={formik.touched.fullName && formik.errors.fullName ? formik.errors.fullName : ""}
                                {...formik.getFieldProps("fullName")}
                            />
                        </Col>

                        <Col md="6" className="mb-3">
                            <NeoInput
                                label="Cargo / Função"
                                name="position"
                                type="text"
                                flat={true}
                                placeholder="Ex: Diretor Executivo, Coordenadora..."
                                error={formik.touched.position && formik.errors.position ? formik.errors.position : ""}
                                {...formik.getFieldProps("position")}
                            />
                        </Col>

                        <Col md="6" className="mb-3">
                            <NeoInput
                                label="E-mail Administrativo *"
                                name="email"
                                type="email"
                                flat={true}
                                placeholder="Ex: felipe@neoapp.com"
                                error={formik.touched.email && formik.errors.email ? formik.errors.email : ""}
                                {...formik.getFieldProps("email")}
                                disabled={!!formik.values.id}
                                title={formik.values.id ? "O e-mail de acesso não pode ser alterado por aqui" : ""}
                            />
                        </Col>

                        <Col md="6" className="mb-3">
                            <NeoInput
                                label="WhatsApp de Contato"
                                name="phone"
                                type="text"
                                flat={true}
                                placeholder="(79) 99999-9999"
                                error={formik.touched.phone && formik.errors.phone ? formik.errors.phone : ""}
                                {...formik.getFieldProps("phone")}
                            />
                        </Col>

                        <Col md="6" className="mb-3">
                            <NeoInput
                                label="Papel de Acesso (Role) *"
                                name="platformRole"
                                type="select"
                                flat={true}
                                error={formik.touched.platformRole && formik.errors.platformRole ? formik.errors.platformRole : ""}
                                {...formik.getFieldProps("platformRole")}
                            >
                                <option value="">Selecione um Papel</option>
                                {ROLE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </NeoInput>
                        </Col>

                        <Col md="6" className="mb-3">
                            <div className="neo-input-wrapper neo-input-wrapper-md">
                                <label className="neo-input-label">Status</label>
                                <div className="form-check form-switch pt-2">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        checked={formik.values.status === "active"}
                                        onChange={(e) => {
                                            formik.setFieldValue("status", e.target.checked ? "active" : "inactive");
                                            onChange(true);
                                        }}
                                        className="form-check-input custom-switch-primary cursor-pointer"
                                        id="user-status-switch"
                                    />
                                    <label className="form-check-label ms-2 small text-muted cursor-pointer" htmlFor="user-status-switch">
                                        {formik.values.status === "active" ? "Acesso Liberado" : "Acesso Bloqueado"}
                                    </label>
                                </div>
                            </div>
                        </Col>

                        {!formik.values.id && (
                            <Col md="12" className="mb-3">
                                <NeoInput
                                    label="Senha Inicial de Acesso *"
                                    name="password"
                                    type="text"
                                    flat={true}
                                    placeholder="Crie uma senha forte"
                                    error={formik.touched.password && formik.errors.password ? formik.errors.password : ""}
                                    {...formik.getFieldProps("password")}
                                />
                            </Col>
                        )}
                    </Row>
                </Col>
            </Row>

            <hr className="my-4 border-dashed" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }} />

            <Row className="mb-4">
                <Col lg={12}>
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-map-marker-outline me-2 font-size-16 text-primary"></i>
                        Endereço Residencial
                    </h6>
                </Col>

                <Col md="3" className="mb-3">
                    <NeoInput
                        label="CEP"
                        name="zipCode"
                        type="text"
                        flat={true}
                        placeholder="00000-000"
                        error={formik.touched.zipCode && formik.errors.zipCode ? formik.errors.zipCode : ""}
                        value={formik.values.zipCode}
                        onChange={handleCepChangeInternal}
                    />
                </Col>

                <Col md="3" className="mb-3">
                    <NeoInput
                        label="Estado (UF)"
                        name="state"
                        type="text"
                        flat={true}
                        placeholder="Ex: SP"
                        error={formik.touched.state && formik.errors.state ? formik.errors.state : ""}
                        {...formik.getFieldProps("state")}
                    />
                </Col>

                <Col md="6" className="mb-3">
                    <NeoInput
                        label="Cidade"
                        name="city"
                        type="text"
                        flat={true}
                        placeholder="Cidade"
                        error={formik.touched.city && formik.errors.city ? formik.errors.city : ""}
                        {...formik.getFieldProps("city")}
                    />
                </Col>

                <Col md="4" className="mb-3">
                    <NeoInput
                        label="Bairro"
                        name="neighborhood"
                        type="text"
                        flat={true}
                        placeholder="Bairro"
                        error={formik.touched.neighborhood && formik.errors.neighborhood ? formik.errors.neighborhood : ""}
                        {...formik.getFieldProps("neighborhood")}
                    />
                </Col>

                <Col md="6" className="mb-3">
                    <NeoInput
                        label="Logradouro / Rua"
                        name="street"
                        type="text"
                        flat={true}
                        placeholder="Rua, Avenida..."
                        error={formik.touched.street && formik.errors.street ? formik.errors.street : ""}
                        {...formik.getFieldProps("street")}
                    />
                </Col>

                <Col md="2" className="mb-3">
                    <NeoInput
                        label="Número"
                        name="number"
                        type="text"
                        flat={true}
                        placeholder="Nº / S/N"
                        error={formik.touched.number && formik.errors.number ? formik.errors.number : ""}
                        {...formik.getFieldProps("number")}
                    />
                </Col>

                <Col md="12" className="mb-3">
                    <NeoInput
                        label="Complemento"
                        name="complement"
                        type="text"
                        flat={true}
                        placeholder="Ex: Apto 302, Bloco B"
                        error={formik.touched.complement && formik.errors.complement ? formik.errors.complement : ""}
                        {...formik.getFieldProps("complement")}
                    />
                </Col>
            </Row>
        </NeoCard>
    );
};

UserEditor.propTypes = {
    user: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};

export default UserEditor;
